import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Bot,
  Plug,
  KeyRound,
  ScrollText,
  Copy,
  Check,
  Trash2,
  Loader,
  BookOpen,
} from 'lucide-react';
import { toast } from '@clareefai/ui';
import type { McpAgentToken, McpAgentTokenAccess, McpAuditOutcome } from '@clareefai/types';
import PageTitle from '../../components/shared/PageTitle';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import {
  useGetMcpConnectionsQuery,
  useRevokeMcpConnectionMutation,
  useGetMcpAgentTokensQuery,
  useCreateMcpAgentTokenMutation,
  useRevokeMcpAgentTokenMutation,
  useGetMcpAuditLogsQuery,
} from '../../store/mcpApi';

type Tab = 'connections' | 'tokens' | 'audit';

const formatDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '—');

function ScopeBadge({ hasWrite }: { hasWrite: boolean }) {
  const { t } = useTranslation();
  return hasWrite ? (
    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      {t('agentAccess.scope.readWrite')}
    </span>
  ) : (
    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      {t('agentAccess.scope.readOnly')}
    </span>
  );
}

const outcomeStyles: Record<McpAuditOutcome, string> = {
  OK: 'bg-green-100 text-green-700',
  ERROR: 'bg-red-100 text-red-700',
  DENIED: 'bg-amber-100 text-amber-700',
  RATE_LIMITED: 'bg-amber-100 text-amber-700',
};

export default function AgentAccessSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('connections');

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> {t('agentAccess.back')}
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-purple-50 flex items-center justify-center">
            <Bot className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <PageTitle title={t('agentAccess.title')} />
            <h1 className="text-xl font-semibold text-gray-900">{t('agentAccess.title')}</h1>
            <p className="text-sm text-gray-500">{t('agentAccess.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/settings/agent-access/guide')}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <BookOpen className="h-4 w-4" /> {t('agentAccess.connectGuide')}
        </button>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <TabButton icon={<Plug className="h-4 w-4" />} label={t('agentAccess.tabs.connections')} active={tab === 'connections'} onClick={() => setTab('connections')} />
        <TabButton icon={<KeyRound className="h-4 w-4" />} label={t('agentAccess.tabs.tokens')} active={tab === 'tokens'} onClick={() => setTab('tokens')} />
        <TabButton icon={<ScrollText className="h-4 w-4" />} label={t('agentAccess.tabs.audit')} active={tab === 'audit'} onClick={() => setTab('audit')} />
      </div>

      {tab === 'connections' && <ConnectionsTab />}
      {tab === 'tokens' && <TokensTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

function TabButton({ icon, label, active, onClick }: { icon: ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${
        active ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-sm text-gray-400 text-center py-10">{text}</p>;
}

// ---------- connections ----------

function ConnectionsTab() {
  const { t } = useTranslation();
  const { data: connections, isLoading } = useGetMcpConnectionsQuery();
  const [revoke, { isLoading: isRevoking }] = useRevokeMcpConnectionMutation();
  const [toRevoke, setToRevoke] = useState<string | null>(null);

  if (isLoading) return <Loading />;
  if (!connections || connections.length === 0) return <EmptyState text={t('agentAccess.connections.empty')} />;

  const confirmRevoke = async () => {
    if (!toRevoke) return;
    try {
      await revoke(toRevoke).unwrap();
      toast.success(t('agentAccess.connections.revoked'));
    } catch {
      toast.error(t('agentAccess.error'));
    } finally {
      setToRevoke(null);
    }
  };

  return (
    <div className="space-y-3">
      {connections.map((c) => (
        <div key={c.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
          <div>
            <p className="font-medium text-gray-900 flex items-center gap-2">
              {c.clientName}
              <ScopeBadge hasWrite={c.scopes.includes('mcp:write')} />
            </p>
            <p className="text-xs text-gray-500">
              {t('agentAccess.connections.connectedAt', { date: formatDate(c.connectedAt) })}
              {' · '}
              {t('agentAccess.connections.lastUsed', { date: formatDate(c.lastUsedAt) })}
            </p>
          </div>
          <button
            onClick={() => setToRevoke(c.id)}
            className="text-sm font-medium text-red-600 hover:text-red-700"
          >
            {t('agentAccess.connections.disconnect')}
          </button>
        </div>
      ))}

      <ConfirmDialog
        isOpen={toRevoke !== null}
        onClose={() => setToRevoke(null)}
        onConfirm={confirmRevoke}
        title={t('agentAccess.connections.confirmTitle')}
        description={t('agentAccess.connections.confirmBody')}
        confirmLabel={t('agentAccess.connections.disconnect')}
        isLoading={isRevoking}
      />
    </div>
  );
}

// ---------- agent tokens ----------

function TokensTab() {
  const { t } = useTranslation();
  const { data: tokens, isLoading } = useGetMcpAgentTokensQuery();
  const [createToken, { isLoading: isCreating }] = useCreateMcpAgentTokenMutation();
  const [revoke, { isLoading: isRevoking }] = useRevokeMcpAgentTokenMutation();

  const [label, setLabel] = useState('');
  const [access, setAccess] = useState<McpAgentTokenAccess>('read');
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [toRevoke, setToRevoke] = useState<McpAgentToken | null>(null);

  const create = async () => {
    if (!label.trim()) return;
    try {
      const res = await createToken({ label: label.trim(), access }).unwrap();
      setFreshToken(res.token);
      setLabel('');
      setAccess('read');
    } catch {
      toast.error(t('agentAccess.error'));
    }
  };

  const copy = () => {
    if (!freshToken) return;
    navigator.clipboard.writeText(freshToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const confirmRevoke = async () => {
    if (!toRevoke) return;
    try {
      await revoke(toRevoke.id).unwrap();
      toast.success(t('agentAccess.tokens.revoked'));
    } catch {
      toast.error(t('agentAccess.error'));
    } finally {
      setToRevoke(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* one-time reveal */}
      {freshToken && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
          <p className="text-sm font-medium text-purple-900 mb-2">{t('agentAccess.tokens.revealTitle')}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-white px-3 py-2 text-xs text-gray-700 border border-purple-200">
              {freshToken}
            </code>
            <button onClick={copy} className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? t('agentAccess.tokens.copied') : t('agentAccess.tokens.copy')}
            </button>
          </div>
          <p className="mt-2 text-xs text-purple-700">{t('agentAccess.tokens.revealWarning')}</p>
          <button onClick={() => setFreshToken(null)} className="mt-3 text-xs font-medium text-purple-700 underline">
            {t('agentAccess.tokens.dismiss')}
          </button>
        </div>
      )}

      {/* create */}
      <div className="space-y-3">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentAccess.tokens.labelField')}</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('agentAccess.tokens.labelPlaceholder')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
            />
          </div>
          <button
            onClick={create}
            disabled={isCreating || !label.trim()}
            className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {isCreating ? t('agentAccess.tokens.creating') : t('agentAccess.tokens.create')}
          </button>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" name="token-access" checked={access === 'read'} onChange={() => setAccess('read')} className="accent-purple-600" />
            {t('agentAccess.tokens.accessRead')}
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" name="token-access" checked={access === 'read_write'} onChange={() => setAccess('read_write')} className="accent-purple-600" />
            {t('agentAccess.tokens.accessReadWrite')}
          </label>
        </div>
        {access === 'read_write' && (
          <p className="text-xs text-amber-600">{t('agentAccess.tokens.writeWarning')}</p>
        )}
      </div>

      {/* list */}
      {isLoading ? (
        <Loading />
      ) : !tokens || tokens.length === 0 ? (
        <EmptyState text={t('agentAccess.tokens.empty')} />
      ) : (
        <div className="space-y-2">
          {tokens.map((tok) => (
            <div key={tok.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
              <div>
                <p className="font-medium text-gray-900 flex items-center gap-2">
                  {tok.label}
                  <ScopeBadge hasWrite={tok.scope.includes('mcp:write')} />
                  {tok.revoked && <span className="text-xs text-gray-400">({t('agentAccess.tokens.revokedTag')})</span>}
                </p>
                <p className="text-xs text-gray-500">
                  {t('agentAccess.tokens.created', { date: formatDate(tok.createdAt) })}
                  {' · '}
                  {t('agentAccess.tokens.expires', { date: formatDate(tok.expiresAt) })}
                  {' · '}
                  {t('agentAccess.tokens.lastUsed', { date: formatDate(tok.lastUsedAt) })}
                </p>
              </div>
              {!tok.revoked && (
                <button onClick={() => setToRevoke(tok)} className="text-gray-400 hover:text-red-600" aria-label={t('agentAccess.tokens.revoke')}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={toRevoke !== null}
        onClose={() => setToRevoke(null)}
        onConfirm={confirmRevoke}
        title={t('agentAccess.tokens.confirmTitle')}
        description={t('agentAccess.tokens.confirmBody', { label: toRevoke?.label ?? '' })}
        confirmLabel={t('agentAccess.tokens.revoke')}
        isLoading={isRevoking}
      />
    </div>
  );
}

// ---------- audit ----------

function AuditTab() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const size = 50;
  const { data, isLoading } = useGetMcpAuditLogsQuery({ page, size });

  if (isLoading) return <Loading />;
  if (!data || data.items.length === 0) return <EmptyState text={t('agentAccess.audit.empty')} />;

  const totalPages = Math.max(1, Math.ceil(data.totalElements / size));

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
            <tr>
              <th className="px-4 py-2">{t('agentAccess.audit.when')}</th>
              <th className="px-4 py-2">{t('agentAccess.audit.tool')}</th>
              <th className="px-4 py-2">{t('agentAccess.audit.outcome')}</th>
              <th className="px-4 py-2">{t('agentAccess.audit.latency')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.items.map((entry, i) => (
              <tr key={i}>
                <td className="px-4 py-2 text-gray-500">{new Date(entry.at).toLocaleString()}</td>
                <td className="px-4 py-2 font-mono text-gray-700">{entry.tool}</td>
                <td className="px-4 py-2">
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${outcomeStyles[entry.outcome]}`}>{entry.outcome}</span>
                </td>
                <td className="px-4 py-2 text-gray-500">{entry.latencyMs} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{t('agentAccess.audit.pageOf', { page: page + 1, total: totalPages })}</span>
        <div className="flex gap-2">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40">
            {t('agentAccess.audit.prev')}
          </button>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40">
            {t('agentAccess.audit.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-10">
      <Loader className="h-6 w-6 animate-spin text-purple-600" />
    </div>
  );
}
